import { useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSnackbar } from 'notistack';

import {
  BulkCreatePromosProps,
  BulkCreatePromosResult,
  CheckPromoQuery,
  CheckPromoResponse,
  CheckPromoResult,
  CreateOrUpdatePromoPayload,
  CreateOrUpdatePromoResponse,
  InternalPromoWithDiscounts,
  PromoConditionTypeConfig,
  PromoLoyaltyMetaConfig,
  PromoMeta,
  PromoRole,
  PromoTag,
  PromoWarning,
  bulkCreatePromos,
  checkPromoRequest,
  createOrUpdatePromo,
  getInternalPromo,
  getPromoById,
  getPromoMeta,
  promoCreateTemplateSessionKey,
  promoRoles,
} from '~/services/PromoService';

import { getPromoTagArrays } from '~/utils/promoTags';

import useLocalStorage from './useLocalStorage';

export type CreateSinglePromoClonePayload = Partial<Omit<BulkCreatePromosProps, 'number_to_create'>>;

type InitPromo =
  | {
      promoId: string;
    }
  | {
      codeName: string;
      brand: string;
    }
  | null;

type UsePromoProps = {
  options?: {
    autoSelectTag?: boolean;
    showErrors?: boolean;
  } | null;
  selectedDepartment?: string | null;
  selectedCategory?: string | null;
  selectedSubCategory?: string | null;
  onPromoError?: (error: string, field: string) => void;
  onPromoUpdate?: ((promo: InternalPromoWithDiscounts | null) => void) | null;
  showPromoWarnings?: boolean;
  onTagSelectionChange?: (field: 'dept_tag' | 'category_tag' | 'sub_category_tag', value: string | null) => void;
};

type PromoCreateTemplate = {
  dept_tag: string | null;
  category_tag: string | null;
  sub_category_tag: string | null;
};

type UsePromoResult = {
  isLoading: boolean;
  isCheckLoading: boolean;
  isPromoBetaUser: boolean;
  errors: Array<string>;
  promo: InternalPromoWithDiscounts | null;
  promoParent: InternalPromoWithDiscounts | null;
  promoWarnings: Array<PromoWarning>;
  promoMeta: PromoMeta | null;
  promoLoyaltyMetaConfig: PromoLoyaltyMetaConfig | null;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig> | null;
  isPromoMetaLoading: boolean;
  promoTagArrays: App.PromoTagArrays | null;
  promoTagSelections: {
    selectedDepartment: string | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
  };
  promoCheckStatus: CheckPromoResult | null;
  usersPromoRoles: Array<PromoRole>;
  createSinglePromoClone: (payload: CreateSinglePromoClonePayload) => Promise<BulkCreatePromosResult>;
  checkPromo: (query: CheckPromoQuery) => Promise<CheckPromoResult>;
  createPromo: (payload: CreateOrUpdatePromoPayload) => Promise<CreateOrUpdatePromoResponse>;
  updatePromo: (payload: CreateOrUpdatePromoPayload) => Promise<CreateOrUpdatePromoResponse>;
  initPromoData: (init: InitPromo, force?: boolean) => Promise<void>;
  setPromoTagSelection: (
    deptTag: string,
    categoryTag?: string,
    subCategoryTag?: string,
  ) => {
    selectedDepartment: string | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
  };
  setShowDevPromoWarnings: (show: boolean) => void;
  showDevPromoWarnings: boolean;
  savePromoCreateTemplate: () => void;
  loadPromoCreateTemplate: () => void;
};

/**
 * Hook for managing promo data and operations
 *
 * Features:
 * - Manages promo creation, updates, and validation
 * - Handles promo tag selections (department, category, sub-category)
 * - Supports localStorage template persistence for promo creation
 *
 * localStorage Template:
 * - Saves tag selections when explicitly called or on successful promo creation (if autoSaveTemplate is enabled)
 * - Automatically loads saved template when creating new promos (if no existing selections)
 * - Template key: 'promo--create-template'
 * - Supported fields: dept_tag, category_tag, sub_category_tag
 *
 * @param props - Configuration options for the hook
 * @returns Object containing promo state and operations
 */
export function usePromo(props?: UsePromoProps): UsePromoResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckLoading, setIsCheckLoading] = useState<boolean>(false);
  const [isPromoBetaUser, setIsPromoBetaUser] = useState<boolean>(false);
  const [errors, setErrors] = useState<Array<string>>([]);
  const [promo, setPromo] = useState<InternalPromoWithDiscounts | null>(null);
  const [promoParent, setPromoParent] = useState<InternalPromoWithDiscounts | null>(null);
  const [promoWarnings, setPromoWarnings] = useState<Array<PromoWarning>>([]);
  const [showDevPromoWarnings, setShowDevPromoWarnings] = useState<boolean>(false);
  const [promoMeta, setPromoMeta] = useState<PromoMeta | null>(null);
  const [promoCodeConditionTypeConfig, setPromoCodeConditionTypeConfig] =
    useState<Array<PromoConditionTypeConfig> | null>(null);
  const [isPromoMetaLoading, setIsPromoMetaLoading] = useState<boolean>(false);
  const [promoTagArrays, setPromoTagArrays] = useState<App.PromoTagArrays | null>(null);

  const [promoCreateTemplate, setPromoCreateTemplate] = useLocalStorage(promoCreateTemplateSessionKey, {
    dept_tag: null,
    category_tag: null,
    sub_category_tag: null,
  } as PromoCreateTemplate);

  const [promoTagSelections, setPromoTagSelections] = useState<{
    selectedDepartment: string | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
  }>({
    selectedDepartment: props?.selectedDepartment || null,
    selectedCategory: props?.selectedCategory || null,
    selectedSubCategory: props?.selectedSubCategory || null,
  });

  const [usersPromoRoles, setUsersPromoRoles] = useState<Array<PromoRole>>([]);
  const [promoLoyaltyMetaConfig, setPromoLoyaltyMetaConfig] = useState<PromoLoyaltyMetaConfig | null>(null);
  const [promoCheckStatus, setPromoCheckStatus] = useState<CheckPromoResponse['result'] | null>(null);
  const [lastCheckedCodeName, setLastCheckedCodeName] = useState<string>('');

  const checkPromoInternal = async (query: CheckPromoQuery): Promise<CheckPromoResponse> => {
    const check = await checkPromoRequest(query);
    setPromoCheckStatus(check.result);
    return check;
  };

  const checkPromo = useCallback(
    debounce((query: CheckPromoQuery) => {
      // Only check if code name is different from last checked
      if (query?.codeName === lastCheckedCodeName) {
        return;
      }

      setIsCheckLoading(true);
      if (query?.codeName?.length == 0) {
        return null;
      }

      // Update the last checked code name
      setLastCheckedCodeName(query.codeName);

      checkPromoInternal(query)
        .then((result) => {
          setPromoCheckStatus(result.result);
          return result.result;
        })
        .finally(() => {
          setIsCheckLoading(false);
        });
    }, 1000),
    [checkPromoInternal, setPromoCheckStatus],
  );

  useEffect(() => {
    return () => {
      checkPromo.cancel();
    };
  }, [checkPromo]);

  // Ensure tag selections are refreshed when both promo and promoMeta are available
  // This handles cases where promoMeta loads after promo, or when promo tags change
  useEffect(() => {
    if (promo && promoMeta && promoMeta.promo_tags) {
      const promoDeptTag = promo.dept_tag || '';
      const promoCategoryTag = promo.category_tag || null;
      const promoSubCategoryTag = promo.sub_category_tag || null;

      // Only refresh if tag values differ from current selections or if we have tag values
      if (
        (promoDeptTag || promoCategoryTag || promoSubCategoryTag) &&
        (promoTagSelections.selectedDepartment !== promoDeptTag ||
          promoTagSelections.selectedCategory !== promoCategoryTag ||
          promoTagSelections.selectedSubCategory !== promoSubCategoryTag)
      ) {
        refreshMetaOptions(promoMeta.promo_tags, promoDeptTag, promoCategoryTag, promoSubCategoryTag);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.dept_tag, promo?.category_tag, promo?.sub_category_tag, promoMeta?.promo_tags]);

  const processPromoLoad = (promo: InternalPromoWithDiscounts) => {
    if (!promoMeta) {
      return;
    }
    if (!promo) {
      return;
    }
    setPromo(promo);
    refreshMetaOptions(promoMeta.promo_tags, promo?.dept_tag, promo?.category_tag, promo?.sub_category_tag);
    setPromoWarnings(promo.promo_warnings.filter((warning) => !warning.is_dev || showDevPromoWarnings));
    props?.onPromoUpdate?.(promo);
  };

  const createPromo = async (payload: CreateOrUpdatePromoPayload): Promise<CreateOrUpdatePromoResponse> => {
    setIsLoading(true);
    try {
      const response = await createOrUpdatePromo({
        ...payload,
        mode: 'create-only',
      });

      processPromoLoad(response.result);

      return response;
    } catch (error) {
      if ('errors' in error && error.errors.length > 0) {
        switch (error.errors[0].code) {
          case 'PROMO_CODE_ALREADY_EXISTS': {
            if (props?.onPromoError) {
              props?.onPromoError('This promo code already exists for this brand', 'code_name');
            } else {
              enqueueSnackbar('This promo code already exists for this brand', { variant: 'error' });
            }
            break;
          }
          case 'PROMO_TOGGLE_ERROR': {
            if (props?.onPromoError) {
              props?.onPromoError(error.errors[0].message, 'promo_toggles');
            } else {
              enqueueSnackbar(error.errors[0].message, { variant: 'error' });
            }
            break;
          }
          case 'INVALID_PROMO_TAGS': {
            if (props?.onPromoError) {
              props?.onPromoError(error.errors[0].message, 'promo_tags');
            } else {
              enqueueSnackbar(error.errors[0].message, { variant: 'error' });
            }
            break;
          }
          default: {
            if (props?.onPromoError) {
              props?.onPromoError(error.errors[0].message, '');
            } else {
              enqueueSnackbar(error.errors[0].message, { variant: 'error' });
            }
            console.warn(`${error.errors[0].code} is not mapped to the nicer frontend error handling`);
          }
        }
      } else {
        enqueueSnackbar(`${JSON.stringify(error, null, 2)}`, { variant: 'error' });
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePromo = async (payload: CreateOrUpdatePromoPayload): Promise<CreateOrUpdatePromoResponse> => {
    setIsLoading(true);
    try {
      const response = await createOrUpdatePromo({
        ...payload,
        mode: 'update-only',
      });

      processPromoLoad(response.result);

      return response;
    } catch (error) {
      enqueueSnackbar(`Error updating promo: ${error.message}`, { variant: 'error' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createSinglePromoClone = async (payload: CreateSinglePromoClonePayload): Promise<BulkCreatePromosResult> => {
    setIsLoading(true);
    try {
      const response = await bulkCreatePromos({
        id_promo_code: payload.id_promo_code ?? '',
        number_to_create: 1,
        promo_prefix: payload.promo_prefix,
        random_post_fix_length: 6,
        ...payload,
      });
      return response;
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const initPromoMeta = async () => {
    if (promoMeta) {
      return;
    }
    setIsPromoMetaLoading(true);
    try {
      const response = await getPromoMeta();

      const { result } = response;
      const roles = result.promo_roles_for_user;
      setPromoMeta(result);
      setPromoCodeConditionTypeConfig(result.promo_condition_types);
      setPromoTagArrays(
        getPromoTagArrays({
          promoTags: result?.promo_tags,
          selectedDepartment: promoTagSelections.selectedDepartment,
          selectedCategory: promoTagSelections.selectedCategory,
          usersPromoRoles: roles,
        }),
      );
      setUsersPromoRoles(roles);
      setPromoLoyaltyMetaConfig(result.promo_loyalty_config);
      setIsPromoMetaLoading(false);

      // Check if user is a Promo-VIP for default values
      const isPromoVip = roles.some((role) => role.roleName === promoRoles.PROMO_VIP_ROLE && role.isActive);

      // Load saved template if no current selections and we have a saved template
      if (
        !promoTagSelections.selectedDepartment &&
        !promoTagSelections.selectedCategory &&
        !promoTagSelections.selectedSubCategory &&
        promoCreateTemplate.dept_tag &&
        result &&
        result.promo_tags
      ) {
        // Load template directly by updating tag arrays and selections
        const templateDeptTag = promoCreateTemplate.dept_tag;
        const templateCategoryTag = promoCreateTemplate.category_tag;
        const templateSubCategoryTag = promoCreateTemplate.sub_category_tag;

        // Update tag arrays with the template department
        const templateTagArrays = getPromoTagArrays({
          promoTags: result.promo_tags,
          selectedDepartment: templateDeptTag,
          selectedCategory: templateCategoryTag,
          usersPromoRoles: roles,
        });

        // Set the tag arrays first
        setPromoTagArrays(templateTagArrays);

        // Then set the selections
        setPromoTagSelections({
          selectedDepartment: templateDeptTag,
          selectedCategory: templateCategoryTag,
          selectedSubCategory: templateSubCategoryTag,
        });
      } else if (isPromoVip && result && result.promo_tags) {
        // (temp rule to support vip users) Set default values for Promo-VIP users
        const vipDeptTag = 'People and Culture';
        const vipCategoryTag = 'Friends and family discount';
        const vipSubCategoryTag = 'N/A';

        // Update tag arrays with the VIP department
        const vipTagArrays = getPromoTagArrays({
          promoTags: result.promo_tags,
          selectedDepartment: vipDeptTag,
          selectedCategory: vipCategoryTag,
          usersPromoRoles: roles,
        });

        // Set the tag arrays first
        setPromoTagArrays(vipTagArrays);

        // Then set the VIP selections
        setPromoTagSelections({
          selectedDepartment: vipDeptTag,
          selectedCategory: vipCategoryTag,
          selectedSubCategory: vipSubCategoryTag,
        });
      }
    } catch (error) {
      console.error('Failed to initialize promo meta:', error);
      setIsPromoMetaLoading(false);
    }
  };

  const refreshMetaOptions = (
    promoTags: Array<PromoTag>,
    selectedDepartment: string,
    selectedCategory: string | null,
    selectedSubCategory?: string | null,
  ) => {
    const tagArrays = getPromoTagArrays({ promoTags, selectedDepartment, selectedCategory, usersPromoRoles });
    setPromoTagArrays(tagArrays);

    const updatedSelections = {
      ...promoTagSelections,
      selectedDepartment,
      selectedCategory: selectedCategory,
      selectedSubCategory: selectedSubCategory !== undefined ? selectedSubCategory : null, // Preserve sub-category if explicitly provided
    };

    // Auto-select category if only one option is available
    if (tagArrays.categoryTags.length == 1) {
      if (props?.options?.autoSelectTag) {
        updatedSelections.selectedCategory = tagArrays.categoryTags[0];
      }
    } else if (tagArrays.categoryTags.length == 0) {
      updatedSelections.selectedCategory = null;
    }

    // For sub-category, we need to get the updated arrays based on the (potentially auto-selected) category
    const finalCategorySelection = updatedSelections.selectedCategory;
    const subCategoryArrays = getPromoTagArrays({
      promoTags,
      selectedDepartment,
      selectedCategory: finalCategorySelection,
      usersPromoRoles,
    });

    if (subCategoryArrays.subCategoryTags.length == 1) {
      if (props?.options?.autoSelectTag) {
        updatedSelections.selectedSubCategory = subCategoryArrays.subCategoryTags[0];
      }
    } else if (subCategoryArrays.subCategoryTags.length == 0) {
      updatedSelections.selectedSubCategory = null;
    }

    // Update the arrays with the final category selection
    setPromoTagArrays(subCategoryArrays);
    setPromoTagSelections(updatedSelections);

    // Call the onTagSelectionChange callback if provided
    if (props?.onTagSelectionChange) {
      props.onTagSelectionChange('dept_tag', updatedSelections.selectedDepartment);
      props.onTagSelectionChange('category_tag', updatedSelections.selectedCategory);
      props.onTagSelectionChange('sub_category_tag', updatedSelections.selectedSubCategory);
    }

    return updatedSelections;
  };

  const setPromoTagSelection = (deptTag: string, categoryTag?: string, subCategoryTag?: string) => {
    // Check if department is changing
    const isDepartmentChanging = deptTag !== promoTagSelections.selectedDepartment;

    const initialSelections = {
      ...promoTagSelections,
      selectedDepartment: deptTag,
      // Clear category and sub-category if department is changing, otherwise use provided values
      selectedCategory: isDepartmentChanging ? null : categoryTag || null,
      selectedSubCategory: isDepartmentChanging ? null : subCategoryTag || null,
    };
    setPromoTagSelections(initialSelections);

    // Only call refreshMetaOptions if promoMeta is available
    if (promoMeta && promoMeta.promo_tags) {
      return refreshMetaOptions(
        promoMeta.promo_tags,
        deptTag,
        initialSelections.selectedCategory,
        initialSelections.selectedSubCategory,
      );
    }

    return initialSelections;
  };

  const initPromoData = async (init: InitPromo, force = false) => {
    if (promo && !force) {
      console.warn('initPromoData (already loaded)', promo);
      return;
    }
    setIsLoading(true);
    setIsPromoMetaLoading(true);
    await initPromoMeta();

    setIsPromoMetaLoading(false);

    if (init == null) {
      setPromo(null);
      processPromoLoad(null);
      props?.onPromoUpdate?.(null);
    } else if ('promoId' in init) {
      try {
        const response = await getPromoById({ idPromo: init.promoId });
        if (response.status == 200) {
          setPromo(response.result);
          processPromoLoad(response.result);
          props?.onPromoUpdate?.(response.result);

          if (response.result.parent_id_promo_code) {
            const parentResponse = await getPromoById({ idPromo: response.result.parent_id_promo_code });
            setPromoParent(parentResponse);
          }
        }
      } catch (error) {
        enqueueSnackbar(`Failed to get promo by ID: ${error.message}`, { variant: 'error' });
      }
    } else if ('codeName' in init) {
      try {
        const response = await getInternalPromo({ code_name: init.codeName, brand: init.brand });
        if (response.status == 200) {
          setPromo(response.result);
          processPromoLoad(response.result);
          props?.onPromoUpdate?.(response.result);

          if (response.result.parent_id_promo_code) {
            const parentResponse = await getPromoById({ idPromo: response.result.parent_id_promo_code });
            setPromoParent(parentResponse);
          }
        }
      } catch (error) {
        enqueueSnackbar(`Failed to get promo by code name: ${error.message}`, { variant: 'error' });
      }
    } else {
      setErrors(['Invalid promo initialization']);
    }

    setIsLoading(false);
  };

  const savePromoCreateTemplate = useCallback(() => {
    setPromoCreateTemplate({
      dept_tag: promoTagSelections.selectedDepartment,
      category_tag: promoTagSelections.selectedCategory,
      sub_category_tag: promoTagSelections.selectedSubCategory,
    });
  }, [promoTagSelections, setPromoCreateTemplate]);

  const loadPromoCreateTemplate = useCallback(() => {
    if (promoCreateTemplate.dept_tag && promoMeta) {
      setPromoTagSelection(
        promoCreateTemplate.dept_tag,
        promoCreateTemplate.category_tag,
        promoCreateTemplate.sub_category_tag,
      );
    }
  }, [promoCreateTemplate, promoMeta, setPromoTagSelection]);

  return {
    isLoading,
    isPromoBetaUser: usersPromoRoles.some((role) => role.roleName == promoRoles.PROMO_BETA_TESTER_ROLE),
    promo,
    promoParent,
    promoMeta,
    promoLoyaltyMetaConfig,
    promoCodeConditionTypeConfig,
    promoTagArrays,
    promoTagSelections,
    setPromoTagSelection,
    errors,
    createSinglePromoClone,
    initPromoData,
    checkPromo,
    usersPromoRoles,
    createPromo,
    updatePromo,
    promoCheckStatus,
    isCheckLoading,
    isPromoMetaLoading,
    promoWarnings,
    setShowDevPromoWarnings,
    showDevPromoWarnings,
    savePromoCreateTemplate,
    loadPromoCreateTemplate,
  };
}
